// src/components/Hero.tsx
import React, { useEffect, useMemo, useState } from "react";
import "./Hero.css";
import { IMAGES } from "../constants/images";

const FADE_MS = 800;

type Slide = {
  image: string;
  title: string;
  subtitle: string;
  href: string;
  kicker?: string;
  overlay?: number;
};

const Hero: React.FC = () => {
  const slides = useMemo<Slide[]>(
    () => [
      {
        image: IMAGES.heroBg,
        title: "Jarjouaa | 1418",
        subtitle: "Jarjouaa | South Lebanon",
        href: "/projects/jarjouaa-1418",
        kicker: "We Are Your Guide For Architecture & Design",
        overlay: 0.25,
      },
      {
        image: "/images/Heros1.jpg",
        title: "I-Land 961 | Beirut, Lebanon",
        subtitle: "I-Land 961 | Beirut, Lebanon",
        href: "/projects/i-land-961",
        kicker: "We Are Your Guide For Architecture & Design",
        overlay: 0.25,
      },
      {
        image: "/images/Heros2.jpg",
        title: "Baawerta Nests",
        subtitle: "Baawerta | Lebanon",
        href: "/projects/baawerta-nests",
        kicker: "We Are Your Guide For Architecture & Design",
        overlay: 0.25,
      },
      {
        image: "/images/rs-btater.webp",
        title: "RS Btater Villa 02",
        subtitle: "RS Btater Villa 02 | Lebanon",
        href: "/projects/rs-btater-villa-02",
        kicker: "We Are Your Guide For Architecture & Design",
        overlay: 0.25,
      },
      {
        image: "/images/kfr-04.webp",
        title: "KFR-426 | Landscape",
        subtitle: "Kfarfakoud | Lebanon",
        href: "/projects/kfr-426-landscape",
        kicker: "We Are Your Guide For Architecture & Design",
        overlay: 0.25,
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const current = slides[index];

  // Preload images
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, [slides]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % slides.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + slides.length) % slides.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  const goPrev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const goNext = () => setIndex((i) => (i + 1) % slides.length);

  return (
    <section className="hero-root">
      {/* Background */}
      <img
        key={current.image}
        src={current.image}
        alt=""
        aria-hidden="true"
        className="hero-bg-img hero-fade-in"
        style={{ animationDuration: `${FADE_MS}ms` }}
      />

      {/* Overlay */}
      <div
        className="hero-overlay"
        style={{ background: `rgba(0,0,0,${current.overlay ?? 0.25})` }}
      />

      {/* Content */}
      <div className="hero-content">
        <p className="hero-kicker">{current.kicker ?? "Our Work"}</p>
        <h1 className="hero-title">
          {current.title.split("|")[0].trim()}{" "}
          <span className="hero-thin">| {current.title.split("|")[1]?.trim() ?? ""}</span>
        </h1>
        <p className="hero-sub">{current.subtitle}</p>

        <div className="button-wrapper">
          <span className="line left-line"></span>
          <a href={current.href} target="_blank" rel="noopener noreferrer" className="hero-button">
            VIEW PROJECT
          </a>
          <span className="line right-line"></span>
        </div>
      </div>

      {/* Arrows */}
      {slides.length > 1 && (
        <>
          <button className="hero-arrow left" aria-label="Previous slide" onClick={goPrev}>
            ‹
          </button>
          <button className="hero-arrow right" aria-label="Next slide" onClick={goNext}>
            ›
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              className={`hero-dot ${i === index ? "active" : ""}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;
